<template>
  <a-tabs
    ref="tabsRef"
    v-bind="attrs"
    :style="getStyle"
    :class="[
      bem.b(),
      bem.is(type === 'button' ? 'button' : ''),
      bem.is(tabWidthMode),
      bem.is(tabBorderRadius ? 'set-border-radius' : ''),
    ]"
    :size="size"
    :hide-add="hideAdd"
    :tab-position="tabPosition"
    :tab-bar-style="getTabBarStyle"
    :type="type === 'button' ? 'card' : type"
  >
    <!-- 默认插槽 -->
    <slot>
      <a-tab-pane
        v-for="(tab, index) in props.tabs"
        :key="tab.key"
        :disabled="tab.disabled"
        :closable="tab.closable"
      >
        <template #tab>
          <slot name="tab" :tab="tab">
            <span>{{ tab.name }}</span>
          </slot>
        </template>
        <slot name="tabPane" :tab="tab" :index="index" />
      </a-tab-pane>
    </slot>

    <!-- 自定义插槽 -->
    <template v-for="item in filterSlots" :key="item" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <!-- tab bar 上左侧额外的元素 -->
    <template #leftExtra>
      <div :class="bem.e('leftExtra')" :style="getLeftExtraStyle">
        <slot name="leftExtra" />
      </div>
    </template>

    <!-- tab bar 上右侧额外的元素 -->
    <template #rightExtra>
      <div :class="bem.e('rightExtra')" :style="getRightExtraStyle">
        <slot name="rightExtra" />
      </div>
    </template>
  </a-tabs>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { computed, ref, useAttrs, useSlots } from 'vue'
import { tabsCardPaddingEnum, tabsLinePaddingEnum, tabsProps } from './tabs'

defineOptions({ name: 'LlTabs' })

const props = defineProps(tabsProps)

const bem = createNamespace('tabs')
const tabsRef = ref()

const attrs = useAttrs()
const slots = useSlots()

// 过滤已声明使用的插槽
const filterSlots = computed(() => {
  const allSlotNames = Object.keys(slots || {})
  return allSlotNames.filter(
    (name) =>
      name !== 'leftExtra' && name !== 'rightExtra' && name !== 'default',
  )
})

const getStyle = computed(() => {
  const computedStyle = {} as any

  computedStyle['--tab-ink-bar-width'] = props.inkBarWidth // 下划线宽度
  // 选中页签下划线颜色
  computedStyle['--tab-ink-bar-color'] = props.inkBarColor
    ? props.inkBarColor
    : props.type === 'button'
      ? '#005ce5'
      : '#2e73ff'

  if (props.tabWidthMode === 'fixed') {
    computedStyle['--tab-fixed-width'] = props.tabFixedWidth
  }

  computedStyle['--tab-border-radius'] = props.tabBorderRadius // 页签边框圆角

  // 页签内边距
  if (props.tabPadding && Object.keys(props.tabPadding).length > 0) {
    for (const key in props.tabPadding) {
      computedStyle[`--tab-${key}`] = props.tabPadding[key]
    }
  } else {
    // 线性
    if (props.type === 'line') {
      // top / bottom
      if (props.tabPosition === 'top' || props.tabPosition === 'bottom') {
        for (const key in tabsLinePaddingEnum.horizontal[props.size]) {
          computedStyle[`--tab-${key}`] =
            tabsLinePaddingEnum.horizontal[props.size][key]
        }
      }
      // left / right
      else {
        for (const key in tabsLinePaddingEnum.vertical[props.size]) {
          computedStyle[`--tab-${key}`] =
            tabsLinePaddingEnum.vertical[props.size][key]
        }
      }
    }
    // 按钮/卡片/可编辑卡片
    else {
      for (const key in tabsCardPaddingEnum[props.size]) {
        computedStyle[`--tab-${key}`] = tabsCardPaddingEnum[props.size][key]
      }
    }
  }

  return {
    width: props.width,
    height: props.height,
    ...computedStyle,
  }
})

// 自定义页签样式
const getTabBarStyle = computed(() => {
  if (props.tabBarStyle) {
    return props.tabBarStyle
  }
  // 组件默认样式
  else {
    switch (props.tabPosition) {
      case 'top':
        return {
          marginBottom: '8px',
        }
      case 'bottom':
        return {
          marginTop: '8px',
        }
      case 'left':
        return {
          marginBottom: '8px',
        }
      case 'right':
        return {
          marginTop: '8px',
        }
      default:
        return null
    }
  }
})

// 左侧缩进
const getLeftExtraStyle = computed(() => {
  return props.tabPosition === 'top' || props.tabPosition === 'bottom'
    ? { marginLeft: props.startIndent + 'px' }
    : { marginTop: props.startIndent + 'px' }
})

// 右侧缩进
const getRightExtraStyle = computed(() => {
  return props.tabPosition === 'top' || props.tabPosition === 'bottom'
    ? { marginRight: props.endIndent + 'px' }
    : { marginBottom: props.endIndent + 'px' }
})
</script>
